import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Package, FileText, TrendingUp, Download, Eye, EyeOff, Share2, LogIn, Clock, BarChart3, MousePointerClick } from 'lucide-react';
import { API, authFetch } from '@/App';
import { toast } from 'sonner';

export default function DataDashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [engagement, setEngagement] = useState(null);
  const [clickStats, setClickStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEmails, setShowEmails] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, usersRes, engagementRes, clicksRes] = await Promise.all([
        authFetch(`${API}/admin/dashboard/stats`),
        authFetch(`${API}/admin/dashboard/users`),
        authFetch(`${API}/admin/dashboard/engagement`),
        authFetch(`${API}/analytics/click-stats`)
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
      if (engagementRes.ok) setEngagement(await engagementRes.json());
      if (clicksRes.ok) setClickStats(await clicksRes.json());
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = (data, filename) => {
    if (!data || data.length === 0) { toast.error('No data to export'); return; }
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(h => {
        const val = row[h];
        if (typeof val === 'string' && (val.includes(',') || val.includes('"'))) return `"${val.replace(/"/g, '""')}"`;
        return val ?? '';
      }).join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Data exported successfully!');
  };

  const mediumLabels = {
    whatsapp: 'WhatsApp', twitter: 'Twitter', facebook: 'Facebook',
    telegram: 'Telegram', copy_link: 'Copy Link', in_app_chat: 'In-App Chat', unknown: 'Other'
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button data-testid="dashboard-back-btn" onClick={() => navigate(-1)} className="p-2 hover:bg-primary/10 rounded-full">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Data Dashboard</h1>
          </div>
          <button
            data-testid="export-users-csv"
            onClick={() => exportCSV(users.map(u => ({
              name: u.name, email: u.email, username: u.username,
              posts: u.post_count, checklists: u.checklist_count,
              followers: u.followers_count, following: u.following_count, joined: u.created_at
            })), 'gloverse_users')}
            className="btn-primary flex items-center gap-2 px-4 py-2"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-5 border border-border">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-7 h-7 text-primary" />
              <span className="text-2xl font-bold">{stats?.total_users || 0}</span>
            </div>
            <p className="text-muted-foreground text-sm">Total Users</p>
          </div>
          <div className="bg-white rounded-xl p-5 border border-border">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-7 h-7 text-blue-500" />
              <span className="text-2xl font-bold">{stats?.total_posts || 0}</span>
            </div>
            <p className="text-muted-foreground text-sm">Total Posts</p>
          </div>
          <div className="bg-white rounded-xl p-5 border border-border">
            <div className="flex items-center gap-3 mb-2">
              <Share2 className="w-7 h-7 text-green-500" />
              <span className="text-2xl font-bold">{engagement?.total_shares_30d || 0}</span>
            </div>
            <p className="text-muted-foreground text-sm">Shares (30d)</p>
          </div>
          <div className="bg-white rounded-xl p-5 border border-border">
            <div className="flex items-center gap-3 mb-2">
              <LogIn className="w-7 h-7 text-orange-500" />
              <span className="text-2xl font-bold">{engagement?.logins_today || 0}</span>
            </div>
            <p className="text-muted-foreground text-sm">Logins Today</p>
          </div>
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-5 border border-border">
            <div className="flex items-center gap-3 mb-2">
              <Package className="w-7 h-7 text-purple-500" />
              <span className="text-2xl font-bold">{stats?.total_checklists || 0}</span>
            </div>
            <p className="text-muted-foreground text-sm">Checklists</p>
          </div>
          <div className="bg-white rounded-xl p-5 border border-border">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-7 h-7 text-teal-500" />
              <span className="text-2xl font-bold">{stats?.total_products || 0}</span>
            </div>
            <p className="text-muted-foreground text-sm">Products in DB</p>
          </div>
          <div className="bg-white rounded-xl p-5 border border-border">
            <div className="flex items-center gap-3 mb-2">
              <Clock className="w-7 h-7 text-indigo-500" />
              <span className="text-2xl font-bold">{engagement?.avg_session_minutes || 0}</span>
            </div>
            <p className="text-muted-foreground text-sm">Avg Min/Day</p>
          </div>
          <div className="bg-white rounded-xl p-5 border border-border">
            <div className="flex items-center gap-3 mb-2">
              <BarChart3 className="w-7 h-7 text-rose-500" />
              <span className="text-2xl font-bold">{engagement?.avg_daily_logins_per_user || 0}</span>
            </div>
            <p className="text-muted-foreground text-sm">Avg Logins/User/Day</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-border overflow-x-auto">
          {['overview', 'clicks', 'engagement', 'users', 'products'].map(tab => (
            <button
              key={tab}
              data-testid={`tab-${tab}`}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 font-medium transition-colors whitespace-nowrap capitalize ${
                activeTab === tab ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground'
              }`}
            >
              {tab === 'users' ? `Users (${users.length})` : tab}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-border">
              <h3 className="font-bold mb-4">Recent Users</h3>
              <div className="space-y-3">
                {users.slice(0, 5).map(user => (
                  <div key={user.user_id} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center font-bold text-primary">{user.name?.charAt(0)}</div>
                    )}
                    <div className="flex-1">
                      <p className="font-medium">{user.name}</p>
                      <p className="text-xs text-muted-foreground">@{user.username || 'no-username'}</p>
                    </div>
                    <span className="text-xs text-muted-foreground">{user.post_count} posts</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 border border-border">
              <h3 className="font-bold mb-4">Most Used Products</h3>
              <div className="space-y-3">
                {stats?.top_products?.slice(0, 5).map((product, idx) => (
                  <div key={product.product_id} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                    <span className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary">{idx + 1}</span>
                    <div className="flex-1">
                      <p className="font-medium text-sm">{product.product_name}</p>
                      <p className="text-xs text-muted-foreground">{product.brand}</p>
                    </div>
                    <span className="text-xs font-medium text-primary">{product.usage_count} uses</span>
                  </div>
                ))}
                {(!stats?.top_products || stats.top_products.length === 0) && (
                  <p className="text-sm text-muted-foreground text-center py-4">No product usage data yet</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Clicks Tab */}
        {activeTab === 'clicks' && (
          <div className="space-y-6">
            {/* Click summary cards */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white rounded-xl p-5 border border-border text-center">
                <p className="text-2xl font-bold text-primary" data-testid="clicks-total">{clickStats?.total_clicks || 0}</p>
                <p className="text-xs text-muted-foreground">Total Clicks</p>
              </div>
              <div className="bg-white rounded-xl p-5 border border-border text-center">
                <p className="text-2xl font-bold text-blue-500" data-testid="clicks-30d">{clickStats?.clicks_30d || 0}</p>
                <p className="text-xs text-muted-foreground">Last 30 Days</p>
              </div>
              <div className="bg-white rounded-xl p-5 border border-border text-center">
                <p className="text-2xl font-bold text-green-500" data-testid="clicks-7d">{clickStats?.clicks_7d || 0}</p>
                <p className="text-xs text-muted-foreground">Last 7 Days</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Top Clicked Products */}
              <div className="bg-white rounded-xl p-6 border border-border">
                <h3 className="font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Top Clicked Products</h3>
                <div className="space-y-3">
                  {clickStats?.top_products?.length > 0 ? clickStats.top_products.map((p, idx) => {
                    const maxClicks = clickStats.top_products[0]?.clicks || 1;
                    const pct = Math.round((p.clicks / maxClicks) * 100);
                    return (
                      <div key={p.product_id || idx} data-testid={`top-click-product-${idx}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium truncate flex-1 mr-2">
                            <span className="text-primary/60 mr-1">#{idx + 1}</span>
                            {p.product_name || 'Unknown'}
                          </span>
                          <span className="text-xs font-bold text-primary shrink-0">{p.clicks} clicks</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-primary/70 rounded-full transition-all" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-[10px] text-muted-foreground shrink-0">{p.brand || ''}</span>
                        </div>
                      </div>
                    );
                  }) : (
                    <p className="text-sm text-muted-foreground text-center py-6">No click data yet. Clicks are tracked when users tap product links in checklists.</p>
                  )}
                </div>
              </div>

              {/* Top Checklists Driving Traffic */}
              <div className="bg-white rounded-xl p-6 border border-border">
                <h3 className="font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Top Checklists (Traffic Drivers)</h3>
                <div className="space-y-3">
                  {clickStats?.top_checklists?.length > 0 ? clickStats.top_checklists.map((cl, idx) => (
                    <div key={cl.checklist_id || idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-testid={`top-click-checklist-${idx}`}>
                      <span className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary shrink-0">{idx + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{cl.checklist_name || cl.checklist_id}</p>
                        <p className="text-xs text-muted-foreground">by @{cl.owner_username || 'unknown'}</p>
                      </div>
                      <span className="text-sm font-bold text-primary shrink-0">{cl.clicks}</span>
                    </div>
                  )) : (
                    <p className="text-sm text-muted-foreground text-center py-6">No checklist click data yet.</p>
                  )}
                </div>
              </div>

              {/* Clicks by Brand */}
              <div className="bg-white rounded-xl p-6 border border-border">
                <h3 className="font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Clicks by Brand</h3>
                <div className="space-y-3">
                  {clickStats?.brand_clicks?.length > 0 ? clickStats.brand_clicks.map((b, idx) => {
                    const maxClicks = clickStats.brand_clicks[0]?.clicks || 1;
                    const pct = Math.round((b.clicks / maxClicks) * 100);
                    return (
                      <div key={b.brand || idx}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium">{b.brand}</span>
                          <span className="text-xs font-bold text-purple-600">{b.clicks} clicks</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-purple-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  }) : (
                    <p className="text-sm text-muted-foreground text-center py-6">No brand click data yet.</p>
                  )}
                </div>
              </div>

              {/* Daily Click Trend */}
              <div className="bg-white rounded-xl p-6 border border-border">
                <h3 className="font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Daily Click Trend (14 days)</h3>
                <div className="space-y-2">
                  {clickStats?.daily_trend?.length > 0 ? clickStats.daily_trend.map(d => {
                    const maxClicks = Math.max(...clickStats.daily_trend.map(t => t.clicks), 1);
                    const pct = Math.round((d.clicks / maxClicks) * 100);
                    return (
                      <div key={d.date} className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground w-20 shrink-0">{d.date.slice(5)}</span>
                        <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-green-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs font-bold w-8 text-right">{d.clicks}</span>
                      </div>
                    );
                  }) : (
                    <p className="text-sm text-muted-foreground text-center py-6">No daily click data yet.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Engagement Tab */}
        {activeTab === 'engagement' && engagement && (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Share Stats by Medium */}
            <div className="bg-white rounded-xl p-6 border border-border">
              <h3 className="font-bold mb-4">Checklist Shares by Medium</h3>
              {engagement.share_by_medium?.length > 0 ? (
                <div className="space-y-3">
                  {engagement.share_by_medium.map(item => {
                    const maxCount = engagement.share_by_medium[0]?.count || 1;
                    const pct = Math.round((item.count / maxCount) * 100);
                    return (
                      <div key={item.medium}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium">{mediumLabels[item.medium] || item.medium}</span>
                          <span className="text-sm font-bold text-primary">{item.count}</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2.5">
                          <div className="bg-primary rounded-full h-2.5 transition-all" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-6">No share data yet. Shares will appear here once users start sharing checklists.</p>
              )}
            </div>

            {/* Login Trend */}
            <div className="bg-white rounded-xl p-6 border border-border">
              <h3 className="font-bold mb-4">Login Activity (Last 30 Days)</h3>
              {engagement.login_trend?.length > 0 ? (
                <div className="space-y-2">
                  {engagement.login_trend.slice(-10).map(item => {
                    const maxLogins = Math.max(...engagement.login_trend.map(d => d.logins), 1);
                    const pct = Math.round((item.logins / maxLogins) * 100);
                    return (
                      <div key={item.date}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-muted-foreground">{new Date(item.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
                          <span className="text-xs">
                            <span className="font-bold">{item.logins}</span> logins, <span className="font-bold">{item.unique_users}</span> users
                          </span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div className="bg-orange-400 rounded-full h-2 transition-all" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-6">No login data yet.</p>
              )}
            </div>

            {/* Top Sharers */}
            <div className="bg-white rounded-xl p-6 border border-border">
              <h3 className="font-bold mb-4">Top Sharers (30 Days)</h3>
              {engagement.top_sharers?.length > 0 ? (
                <div className="space-y-3">
                  {engagement.top_sharers.map((s, idx) => (
                    <div key={s._id} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                      <span className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-xs font-bold text-green-600">{idx + 1}</span>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{s.name}</p>
                        <p className="text-xs text-muted-foreground">@{s.username}</p>
                      </div>
                      <span className="text-xs font-bold text-green-600">{s.share_count} shares</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-6">No share data yet.</p>
              )}
            </div>

            {/* Session Time */}
            <div className="bg-white rounded-xl p-6 border border-border">
              <h3 className="font-bold mb-4">User Session Time (7 Days)</h3>
              {engagement.session_stats?.length > 0 ? (
                <div className="space-y-3">
                  {engagement.session_stats.map((s, idx) => (
                    <div key={s._id} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                      <span className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-bold text-indigo-600">{idx + 1}</span>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{s.name}</p>
                        <p className="text-xs text-muted-foreground">@{s.username}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-bold text-indigo-600">{s.total_minutes} min total</p>
                        <p className="text-[10px] text-muted-foreground">{Math.round(s.avg_minutes_per_day)} min/day avg</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-6">Session tracking will appear here as users spend time on the platform.</p>
              )}
            </div>

            {/* Shares Over Time */}
            {engagement.shares_over_time?.length > 0 && (
              <div className="bg-white rounded-xl p-6 border border-border md:col-span-2">
                <h3 className="font-bold mb-4">Shares Over Time (30 Days)</h3>
                <div className="flex items-end gap-1 h-32">
                  {engagement.shares_over_time.map(item => {
                    const maxC = Math.max(...engagement.shares_over_time.map(d => d.count), 1);
                    const h = Math.max((item.count / maxC) * 100, 4);
                    return (
                      <div key={item.date} className="flex-1 flex flex-col items-center gap-1" title={`${item.date}: ${item.count} shares`}>
                        <span className="text-[9px] text-muted-foreground">{item.count}</span>
                        <div className="w-full bg-primary/80 rounded-t-sm" style={{ height: `${h}%` }} />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-xl border border-border overflow-hidden">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="font-bold">All Users</h3>
              <button
                onClick={() => setShowEmails(!showEmails)}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
              >
                {showEmails ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                {showEmails ? 'Hide Emails' : 'Show Emails'}
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">User</th>
                    {showEmails && <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Email</th>}
                    <th className="px-4 py-3 text-center text-sm font-medium text-muted-foreground">Posts</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-muted-foreground">Checklists</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-muted-foreground">Followers</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Joined</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {users.map(user => (
                    <tr key={user.user_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {user.picture ? (
                            <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary">{user.name?.charAt(0)}</div>
                          )}
                          <div>
                            <p className="font-medium text-sm">{user.name}</p>
                            <p className="text-xs text-muted-foreground">@{user.username || 'no-username'}</p>
                          </div>
                        </div>
                      </td>
                      {showEmails && <td className="px-4 py-3 text-sm">{user.email}</td>}
                      <td className="px-4 py-3 text-center text-sm">{user.post_count}</td>
                      <td className="px-4 py-3 text-center text-sm">{user.checklist_count}</td>
                      <td className="px-4 py-3 text-center text-sm">{user.followers_count}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="bg-white rounded-xl p-6 border border-border">
            <h3 className="font-bold mb-4">Product Usage Analytics</h3>
            <div className="space-y-3">
              {stats?.top_products?.map((product, idx) => (
                <div key={product.product_id} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                  <span className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white font-bold">{idx + 1}</span>
                  <div className="flex-1">
                    <p className="font-medium">{product.product_name}</p>
                    <p className="text-sm text-muted-foreground">{product.brand} • {product.price}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-primary">{product.usage_count}</p>
                    <p className="text-xs text-muted-foreground">times used</p>
                  </div>
                </div>
              ))}
              {(!stats?.top_products || stats.top_products.length === 0) && (
                <p className="text-sm text-muted-foreground text-center py-6">No product usage data yet</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
